import { useState } from 'react';
import { Copy, Check, X, Hash } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, formatBytes, readFileAsArrayBuffer } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

const ALGOS = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'] as const;
type Algo = (typeof ALGOS)[number];

async function digest(buf: ArrayBuffer, algo: Algo): Promise<string> {
  const out = await crypto.subtle.digest(algo, buf);
  return Array.from(new Uint8Array(out))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface Result {
  file: File;
  hashes: Record<Algo, string>;
}

export function FileChecksum() {
  const [results, setResults] = useState<Result[]>([]);
  const [working, setWorking] = useState(false);
  const [algo, setAlgo] = useState<Algo>('SHA-256');
  const [verify, setVerify] = useState('');

  async function handleFiles(files: File[]) {
    setWorking(true);
    try {
      const out: Result[] = [];
      for (const f of files) {
        const buf = await readFileAsArrayBuffer(f);
        const hashes = {} as Record<Algo, string>;
        for (const a of ALGOS) hashes[a] = await digest(buf, a);
        out.push({ file: f, hashes });
      }
      setResults((prev) => [...prev, ...out]);
    } finally {
      setWorking(false);
    }
  }

  function reset() {
    setResults([]);
    setVerify('');
  }

  return (
    <div>
      <ToolHeader
        toolId="file-checksum"
        title="File checksum verifier"
        description="Compute SHA-1/256/384/512 for any file. Verify against an expected hash. Multiple files supported."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <Dropzone
            multiple
            onFiles={handleFiles}
            hint="Any file — runs locally with Web Crypto"
            icon={<Hash size={20} />}
          />
        </Card>
        <Card className="space-y-3">
          <Tabs value={algo} onValueChange={(v) => setAlgo(v as Algo)}>
            <TabsList className="w-full">
              {ALGOS.map((a) => (
                <TabsTrigger key={a} value={a} className="flex-1 text-2xs">
                  {a}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Field label="Verify against (optional)">
            <Input
              value={verify}
              onChange={(e) => setVerify(e.target.value.trim().toLowerCase())}
              placeholder="paste expected hash…"
              className="font-mono text-xs"
            />
          </Field>
          <div className="text-2xs text-fg-subtle pt-2 border-t border-border">
            {results.length > 0 ? `${results.length} file${results.length === 1 ? '' : 's'} hashed` : 'No files yet'}
          </div>
          {results.length > 0 && (
            <Button variant="ghost" size="sm" className="w-full" onClick={reset}>
              Clear all
            </Button>
          )}
        </Card>
      </div>

      {working && (
        <div className="mt-4 text-center text-xs text-fg-subtle">Hashing…</div>
      )}

      {results.length > 0 && (
        <div className="mt-4 space-y-2">
          {results.map((r, i) => (
            <Card key={i} className="!p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.file.name}</div>
                  <div className="text-2xs text-fg-subtle font-mono mt-0.5">
                    {formatBytes(r.file.size)}
                  </div>
                </div>
                {verify && (
                  <Badge variant={r.hashes[algo] === verify ? 'success' : 'danger'}>
                    {r.hashes[algo] === verify ? (
                      <>
                        <Check size={10} /> match
                      </>
                    ) : (
                      <>
                        <X size={10} /> no match
                      </>
                    )}
                  </Badge>
                )}
              </div>
              <div className="space-y-1.5">
                {ALGOS.map((a) => (
                  <div
                    key={a}
                    className={cn(
                      'flex items-center gap-3 px-3 py-1.5 rounded-md group transition',
                      a === algo ? 'bg-accent/5 border border-accent/20' : 'bg-bg-inset border border-border',
                    )}
                  >
                    <span className="w-16 shrink-0 text-2xs font-mono font-semibold uppercase text-fg-muted">
                      {a}
                    </span>
                    <code className="flex-1 text-xs font-mono break-all">{r.hashes[a]}</code>
                    <button
                      onClick={async () => {
                        const ok = await copyToClipboard(r.hashes[a]);
                        if (ok) toast.success('Copied');
                      }}
                      className="opacity-0 group-hover:opacity-100 transition size-6 rounded text-fg-subtle hover:text-fg flex items-center justify-center shrink-0"
                    >
                      <Copy size={10} />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileChecksum;
